import { Device, ID } from '../types/models'
import { Box, Button, Dialog, DialogTitle, IconButton, Typography, useTheme } from '@mui/material'
import { Stack } from '@mui/system'
import { useNavigate } from 'react-router-dom'
import { ArrowForwardIos, Delete, RestartAlt, Settings, Wifi } from '@mui/icons-material'
import { Trans, useTranslation } from 'react-i18next'
import { DeviceIcon } from './atoms/DeviceIcon'
import { isDebug } from '../utils/misc'
import { useDevices } from '../hooks/useDevices'
import { LinkTo } from './atoms/LinkTo'
import { useApp } from '../hooks/useApp'
import { useState } from 'react'

export interface DevicePanelProps {
  device: Device
  className?: string
  url?: string
  onDelete?: () => void
}

/**
 * Summarize device status.
 */
export function DevicePanel(props: DevicePanelProps): React.ReactElement {

  const { device, url, onDelete, className } = props
  const theme = useTheme()
  const navigate = useNavigate()
  const { control } = useDevices()
  const { t } = useTranslation()
  const app = useApp()
  const [rebootable, setRebootable] = useState(false)

  const devCtrl = control.getDeviceControl(device.id as ID)
  const online = !!devCtrl.device.values?.online
  const isUpgrading = devCtrl.isUpgrading()

  const onReboot = async () => {
    const fwInfo = control.ports({ portType: 'FW_INFO', deviceId: device?.id })
    if (fwInfo.length) {
      await fwInfo[0].sendCommand({ name: 'reboot', value: null })
    }
    setRebootable(false)
  }

  return (
    <Box data-cy={`box-${device.hw_id}`} className={className} sx={{
      backgroundColor: theme.palette.panelBackground.main,
      boxShadow: theme.boxShadow.main,
      borderRadius: '1.25rem',
      mb: 2,
    }}>
      <Box sx={{
        display: 'grid',
        gridTemplateColumns: `${device.values?.slave_of ? '2.5em ' : ''}4rem auto 6rem 5rem${app.userHas('PRO_CONFIG_PAGE') ? ' 2rem' : ''}`,
        alignItems: 'center',
        paddingLeft: '1rem',
        height: '5rem',
      }}>
        {device.values?.slave_of && <Wifi/>}
        <DeviceIcon sx={{ mt: 1, width: '2.5rem' }} model={device.device_spec?.model || ''} />
        <Stack>
          <Typography sx={{ color: theme.palette.panelText.main }}>
            {device.name}
            {isDebug('port-numbers') ? ` [#${device.id}]` : ''}
            {app.installerLevel && device && device.id && control.getDeviceControl(device.id).hasProblems() &&
              <LinkTo sx={{ ml: 2, color: 'error.light' }} url={`/config/${device.id}`} title={t('Check Settings')}/>}
          </Typography>
          {device.values?.lan_ip &&
            <Typography data-cy={`lan-ip-${device.hw_id}`} sx={{ color: theme.palette.grey[600] }}>
              {device.values.lan_ip}
            </Typography>}
        </Stack>
        {
          device.ports &&
            <Box sx={{ textAlign: 'center', color: isUpgrading ? 'warning.light' : (online ? 'deviceOnline.main' : 'error.light'), fontWeight: 600 }}>
              {isUpgrading ? t('Upgrading') : (online ? t('Online') : t('Offline'))}
            </Box>
        }
        {
          !device.ports && <Box sx={{ textAlign: 'right', mr: 2 }}>{
            onDelete && <IconButton onClick={onDelete}><Delete/></IconButton>
          }</Box>
        }
        <Box>
          <IconButton onClick={() => setRebootable(true)} sx={{ m: 1, p: 0 }}>
            <RestartAlt data-cy="icon-reboot" sx={{ color: 'blue', cursor: 'pointer' }}/>
          </IconButton>
          {app.installerLevel && <IconButton onClick={() => navigate(`/radio/${device.id}`)} sx={{ m: 0, p: 0 }} disabled={isUpgrading}>
            <Settings data-cy={`icon-Settings-${device.hw_id}`} sx={{ cursor: 'pointer' }}/>
          </IconButton>}
        </Box>
        {app.userHas('PRO_CONFIG_PAGE') && <ArrowForwardIos data-cy="icon-Config" sx={{ cursor: 'pointer' }} onClick={() => url && navigate(url)}/>}
      </Box>
      {
        isDebug('config-diff') &&
        <Box sx={{ p: 2 }}>
          Configuration differencies:
          <pre>
            {JSON.stringify(control.getDeviceControl(device.id as ID).hardwareDiff(), null, 2)}
          </pre>
        </Box>
      }

      <Dialog open={!!rebootable} onClose={() => setRebootable(false)}>
        <DialogTitle sx={{ backgroundColor: 'blue' }}><Trans>Reboot Device?</Trans></DialogTitle>
        <Box sx={{ p: 2 }}>
          <Typography variant="h2">{device && device.hw_id}</Typography>
          <Trans>Are you sure that you want to reboot device?</Trans>
          <Stack direction="row" spacing={2} sx={{ m: 2 }}>
            <Button color="primary" onClick={() => setRebootable(false)}><Trans>Cancel</Trans></Button>
            <Button data-cy="button-Reboot device OK" color="secondary" onClick={onReboot}><Trans>OK</Trans></Button>
          </Stack>
        </Box>
      </Dialog>

    </Box>
  )
}
